import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';

import { useIntake } from '../context/IntakeContext';
import { useStepNav } from '../hooks/useStepNav';
import StepLayout from '../components/StepLayout';
import PhotoGrid from '../components/PhotoGrid';
import { Button } from '../components/ui/button';

const Section = ({ title, onEdit, children }) => (
  <section className="rounded-xl bg-muted/50 p-4">
    <div className="flex items-center justify-between gap-3">
      <h2 className="font-medium">{title}</h2>
      <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
        <Pencil className="h-3.5 w-3.5" />
        Edit
      </Button>
    </div>
    <dl className="mt-2 flex flex-col gap-1.5 text-sm">{children}</dl>
  </section>
);

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex gap-3">
      <dt className="w-28 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 break-words">{value}</dd>
    </div>
  );
};

const ReviewStep = () => {
  const { form, photos, uploads, submit, loadMissingPreviews } = useIntake();
  const { teamId } = useParams();
  const navigate = useNavigate();
  const go = useStepNav();
  const [busy, setBusy] = useState(false);

  // A customer resuming on a different device has photo records but no local
  // previews — fetch them so the review page shows what is actually attached.
  useEffect(() => { loadMissingPreviews(); }, [loadMissingPreviews]);

  const handleSubmit = async () => {
    if (uploads.length) {
      toast.warning('Some photos are still uploading — give it a moment');
      return;
    }

    setBusy(true);
    try {
      await submit({ photosPromised: form.photosPromised || (photos.length ? 'now' : 'none') });
      navigate(`/${teamId}/form/done`, { replace: true });
    } catch (err) {
      toast.error(err.message || 'We could not send your request. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const vehicle = [form.year, form.make, form.model].filter(Boolean).join(' ');
  const services = Object.entries(form.selectedServices || {})
    .filter(([, selected]) => selected)
    .length;

  return (
    <StepLayout
      title="Check and send"
      onBack={() => go('photos')}
      onNext={handleSubmit}
      nextLabel="Send to the repairer"
      busy={busy}
    >
      <Section title="Your details" onEdit={() => go('contact')}>
        <Row label="Name" value={`${form.firstName} ${form.lastName}`.trim()} />
        <Row label="Phone" value={form.phone} />
        <Row label="Email" value={form.email} />
      </Section>

      <Section title="Vehicle" onEdit={() => go('vehicle')}>
        <Row label="Registration" value={form.registration} />
        <Row label="Vehicle" value={vehicle} />
        <Row label="Colour" value={form.colour} />
      </Section>

      <Section title="Damage" onEdit={() => go('damage')}>
        <Row label="What happened" value={form.damageDescription} />
        <Row label="Insurance" value={form.isInsuranceWork ? 'Insurance claim' : 'Paying privately'} />
        <Row label="Claim lodged" value={form.claimMade === 'yes' ? 'Yes' : form.claimMade === 'not-yet' ? 'Not yet' : ''} />
        <Row label="Excess" value={form.excess ? `$${form.excess}` : ''} />
        <Row label="Loan vehicle" value={form.needsLoanVehicle ? 'Requested' : ''} />
        <Row label="Extras" value={services ? `${services} selected` : ''} />
      </Section>

      <section className="rounded-xl bg-muted/50 p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-medium">Photos ({photos.length})</h2>
          <Button type="button" variant="ghost" size="sm" onClick={() => go('photos')}>
            <Pencil className="h-3.5 w-3.5" />
            Add or change
          </Button>
        </div>
        <div className="mt-3">
          <PhotoGrid emptyHint="No photos yet — the repairer may need to arrange a look at the car" />
        </div>
      </section>
    </StepLayout>
  );
};

export default ReviewStep;
